import React, { useMemo } from 'react';
import useMeasure from 'react-use-measure';

const containerPatternBaseWidth = 66.24;
const containerPatternBaseHeight = 120.96;

const containerPatternSvg = `
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${containerPatternBaseWidth}" height="${containerPatternBaseHeight}" viewBox="0 0 ${containerPatternBaseWidth} ${containerPatternBaseHeight}" preserveAspectRatio="none">
  <defs>
    <pattern id="pattern0_428_6927" patternUnits="userSpaceOnUse" patternTransform="matrix(${containerPatternBaseWidth} 0 0 ${containerPatternBaseHeight} 0 0)" preserveAspectRatio="none" viewBox="0 0 ${containerPatternBaseWidth} ${containerPatternBaseHeight}" width="1" height="1">
      <use xlink:href="#pattern0_428_6927_inner" transform="translate(-99.36 -60.48)" />
      <use xlink:href="#pattern0_428_6927_inner" transform="translate(-33.12 -60.48)" />
      <use xlink:href="#pattern0_428_6927_inner" transform="translate(33.12 -60.48)" />
      <use xlink:href="#pattern0_428_6927_inner" transform="translate(-132.48 0)" />
      <use xlink:href="#pattern0_428_6927_inner" transform="translate(-66.24 0)" />
      <g id="pattern0_428_6927_inner">
        <foreignObject x="-12" y="-12" width="1304" height="744"><div xmlns="http://www.w3.org/1999/xhtml" style="backdrop-filter:blur(6px);height:100%;width:100%"></div></foreignObject>
        <path data-figma-bg-blur-radius="12" d="M33 4.04102C34.8563 2.96936 37.1437 2.96936 39 4.04102L62.1768 17.4229C64.0332 18.4946 65.1768 20.4755 65.1768 22.6191V49.3809C65.1768 51.5245 64.0332 53.5054 62.1768 54.5771L39 67.959C37.2595 68.9638 35.1403 69.0259 33.3525 68.1465L33 67.959L9.82324 54.5771C7.96684 53.5054 6.82324 51.5245 6.82324 49.3809V22.6191C6.82324 20.4755 7.96684 18.4946 9.82324 17.4229L33 4.04102Z" fill="#FDC700" fill-opacity="0.25" stroke="#D08700" stroke-width="4" />
      </g>
      <use xlink:href="#pattern0_428_6927_inner" transform="translate(-99.36 60.48)" />
      <use xlink:href="#pattern0_428_6927_inner" transform="translate(-33.12 60.48)" />
      <use xlink:href="#pattern0_428_6927_inner" transform="translate(33.12 60.48)" />
    </pattern>
  </defs>
  <rect width="100%" height="100%" fill="url(#pattern0_428_6927)" />
</svg>`;

const containerPatternDataUri = `url("data:image/svg+xml,${encodeURIComponent(containerPatternSvg)}")`;
const containerPatternSize = `${containerPatternBaseWidth}px ${containerPatternBaseHeight}px`;

type RoundedOctagonProps = {
  cornerDiagonal: number; // fixed length for each slanted side (px)
  cornerRadius?: number; // rounding radius (px)
  stroke?: string;
  strokeWidth?: number;
  background?: string;
  strokeGradient?: string[]; // optional gradient colors for outline
  strokeGradientAngle?: number; // degrees
  // Advanced gradient control for outline
  strokeGradientId?: string; // reference existing <linearGradient id="...">
  strokeGradientUnits?: 'objectBoundingBox' | 'userSpaceOnUse';
  strokeGradientCoords?: { x1: number; y1: number; x2: number; y2: number };
  strokeGradientStops?: Array<{ offset?: number; color: string }>; // explicit stops (0..1)
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode; // masked content
};

const buildRoundedPath = (
  width: number,
  height: number,
  cornerDiagonal: number,
  radius: number
) => {
  const w = Math.max(1, width);
  const h = Math.max(1, height);

  // Convert fixed slanted-side length to inset distance along axes.
  const insetRaw = cornerDiagonal / Math.SQRT2;
  const inset = Math.max(0, Math.min(insetRaw, w / 2, h / 2));

  // Base octagon vertices (clockwise)
  const pts: Array<[number, number]> = [
    [inset, 0],
    [w - inset, 0],
    [w, inset],
    [w, h - inset],
    [w - inset, h],
    [inset, h],
    [0, h - inset],
    [0, inset],
  ];

  // Edge lengths for clamping radius (between successive points)
  const len = (a: [number, number], b: [number, number]) =>
    Math.hypot(b[0] - a[0], b[1] - a[1]);
  const edges = pts.map((p, i) => len(p, pts[(i + 1) % pts.length]));

  // All internal angles are 135 degrees in a regular-cut octagon
  const theta = (135 * Math.PI) / 180;
  const tanHalf = Math.tan(theta / 2); // ~2.414213562

  // Clamp radius so arc fits on both adjacent edges for all corners
  const rCap = pts.reduce((acc, _p, i) => {
    const ePrev = edges[(i - 1 + edges.length) % edges.length];
    const eNext = edges[i];
    const cap = Math.min(ePrev, eNext) * 0.5 * tanHalf;
    return Math.min(acc, cap);
  }, Number.POSITIVE_INFINITY);
  const rEff = Math.max(0, Math.min(radius, rCap));
  const t = rEff / tanHalf;

  // Helper to normalize a vector
  const norm = (dx: number, dy: number) => {
    const m = Math.hypot(dx, dy) || 1;
    return [dx / m, dy / m];
  };

  // For each vertex i, compute entry (i1) and exit (i2) points offset by t
  const corners = pts.map((p, i) => {
    const prev = pts[(i - 1 + pts.length) % pts.length];
    const next = pts[(i + 1) % pts.length];
    const uPrev = norm(prev[0] - p[0], prev[1] - p[1]); // towards prev
    const uNext = norm(next[0] - p[0], next[1] - p[1]); // towards next
    const i1: [number, number] = [p[0] + uPrev[0] * t, p[1] + uPrev[1] * t];
    const i2: [number, number] = [p[0] + uNext[0] * t, p[1] + uNext[1] * t];
    return { i1, i2 };
  });

  // Build path: start at corner 0's exit, then line to next entry and arc each corner
  const p0 = corners[0].i2;
  let d = `M ${p0[0]} ${p0[1]}`;
  for (let k = 1; k <= pts.length; k++) {
    const idx = k % pts.length;
    const { i1, i2 } = corners[idx];
    d += ` L ${i1[0]} ${i1[1]}`; // straight edge
    // Arc: rx ry 0 large-arc-flag sweep-flag x y
    d += ` A ${rEff} ${rEff} 0 0 1 ${i2[0]} ${i2[1]}`;
  }
  d += ' Z';
  return d;
};

export const RoundedOctagon: React.FC<RoundedOctagonProps> = ({
  cornerDiagonal,
  cornerRadius = 12,
  stroke = '#111827',
  strokeWidth = 2,
  background = 'transparent',
  strokeGradient,
  strokeGradientAngle = 135,
  strokeGradientId,
  strokeGradientUnits = 'objectBoundingBox',
  strokeGradientCoords,
  strokeGradientStops,
  className,
  style,
  children,
}) => {
  const [elementRef, bounds] = useMeasure();

  const width = bounds.width;
  const height = bounds.height;

  const gid = Math.random().toString(36).slice(2);
  const d = useMemo(
    () => buildRoundedPath(width, height, cornerDiagonal, cornerRadius),
    [width, height, cornerDiagonal, cornerRadius]
  );

  const clipPathValue = `path('${d}')`;
  const [x1, y1, x2, y2] = useMemo(() => {
    if (strokeGradientUnits === 'userSpaceOnUse' && strokeGradientCoords) {
      return [strokeGradientCoords.x1, strokeGradientCoords.y1, strokeGradientCoords.x2, strokeGradientCoords.y2];
    }
    const rad = (strokeGradientAngle * Math.PI) / 180;
    const cx = 0.5, cy = 0.5;
    const dx = Math.cos(rad) * 0.5;
    const dy = Math.sin(rad) * 0.5;
    return [cx - dx, cy - dy, cx + dx, cy + dy];
  }, [strokeGradientAngle, strokeGradientUnits, strokeGradientCoords]);

  return (
    <div ref={elementRef} className={className} style={{ position: 'relative', height: '100%',  ...style }}>
      {/* Content layer clipped with CSS, so HTML children work */}
      <div
        className={className}
        style={{
          position: 'relative',
          WebkitClipPath: clipPathValue as any,
          clipPath: clipPathValue as any,
          background,
          backdropFilter: `blur(5px)`,
          height: '100%',
        }}
      >
        {children}
      </div>

      {/* Outline overlay with optional gradient; overflow visible to avoid clipping */}
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}
      >
        {!strokeGradientId && (strokeGradientStops?.length || strokeGradient?.length) && (
          <defs>
            <linearGradient id={`oct-stroke-${gid}`} x1={x1} y1={y1} x2={x2} y2={y2} gradientUnits={strokeGradientUnits}>
              {(strokeGradientStops && strokeGradientStops.length > 0
                ? strokeGradientStops
                : (strokeGradient || []).map((c, i, arr) => ({ color: c, offset: arr.length === 1 ? 0 : i / (arr.length - 1) }))
              ).map((s, i) => (
                <stop key={i} offset={`${((s.offset ?? 0) * 100).toFixed(3)}%`} stopColor={s.color} />
              ))}
            </linearGradient>
          </defs>
        )}
        <path
          d={d}
          fill="none"
          stroke={strokeGradientId
            ? `url(#${strokeGradientId})`
            : (strokeGradientStops?.length || strokeGradient?.length)
              ? `url(#oct-stroke-${gid})`
              : stroke}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          shapeRendering="geometricPrecision"
        />
      </svg>

    </div>
  );
};

export const ContainerShape = ({
  className,
  cornerDiagonal,
  cornerRadius,
  strokeWidth,
  backgroundColor = '#432004',
  children
}: { 
  className?: string;
  cornerDiagonal: number;
  cornerRadius: number;
  strokeWidth: number;
  backgroundColor?: string;
  children?: React.ReactNode 
}) => {
  const [bgRef, bgBounds] = useMeasure();
  const bgSize = useMemo(() => {
    const w = Math.max(1, bgBounds.width || 0);
    const h = Math.max(1, bgBounds.height || 0);
    if (!bgBounds.width || !bgBounds.height) return containerPatternSize;

    let scale = Math.min(w / containerPatternBaseWidth, h / containerPatternBaseHeight);
    if (scale > 5) {
      scale = 1
    } else {
      scale = 0.3
    }

    const scaledW = containerPatternBaseWidth * scale;
    const scaledH = containerPatternBaseHeight * scale;
    return `${scaledW}px ${scaledH}px`;
  }, [bgBounds.width, bgBounds.height]);

  return (
    <RoundedOctagon
      className={className}
      cornerDiagonal={cornerDiagonal}
      cornerRadius={cornerRadius}
      strokeWidth={strokeWidth}
      strokeGradientUnits="userSpaceOnUse"
      strokeGradientCoords={{ x1: 285.703, y1: 18, x2: 198.399, y2: 206.09 }}
      strokeGradientStops={[
      { offset: 0, color: '#FFC364' },
      { offset: 0.264423, color: '#B97200' },
      { offset: 0.843917, color: '#A65F00' },
      ]}
    >
      <div className='absolute inset-0 opacity-[0.8]' style={{ backgroundColor }} />
      <div
        ref={bgRef}
        className='absolute inset-0'
        style={{
          backgroundImage: containerPatternDataUri,
          backgroundRepeat: 'repeat',
          backgroundSize: bgSize,
          backgroundColor,
          opacity: 0.03,
        }}
      />
      <div className='relative h-full'>
        {children}
      </div>
    </RoundedOctagon>
  )
}

// ---- Rounded Hexagon ----
type RoundedHexagonProps = {
  cornerDiagonal: number; // horizontal inset for top/bottom corners (px)
  cornerRadius?: number; // rounding radius (px)
  stroke?: string;
  strokeWidth?: number;
  background?: string;
  strokeGradient?: string[]; // optional gradient colors for outline
  strokeGradientAngle?: number; // degrees
  // Advanced gradient control for outline
  strokeGradientId?: string; // reference existing <linearGradient id="...">
  strokeGradientUnits?: 'objectBoundingBox' | 'userSpaceOnUse';
  strokeGradientCoords?: { x1: number; y1: number; x2: number; y2: number };
  strokeGradientStops?: Array<{ offset?: number; color: string }>; // explicit stops (0..1)
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode; // masked content
};

// Generic rounded-path builder for a convex polygon
const buildRoundedPathFromPoints = (
  ptsIn: Array<[number, number]>,
  radius: number
) => {
  const n = ptsIn.length;
  if (n < 3) return '';

  const len = (a: [number, number], b: [number, number]) => Math.hypot(b[0] - a[0], b[1] - a[1]);
  const norm = (dx: number, dy: number) => {
    const m = Math.hypot(dx, dy) || 1;
    return [dx / m, dy / m] as [number, number];
  };
  const angleBetween = (u: [number, number], v: [number, number]) => {
    const d = Math.max(-1, Math.min(1, u[0] * v[0] + u[1] * v[1]));
    return Math.acos(d);
  };

  // Precompute corners with per-corner effective radius
  const corners = ptsIn.map((p, i) => {
    const prev = ptsIn[(i - 1 + n) % n];
    const next = ptsIn[(i + 1) % n];

    const vPrev: [number, number] = [prev[0] - p[0], prev[1] - p[1]]; // from p to prev
    const vNext: [number, number] = [next[0] - p[0], next[1] - p[1]]; // from p to next
    const uPrev = norm(vPrev[0], vPrev[1]);
    const uNext = norm(vNext[0], vNext[1]);

    const alpha = angleBetween(uPrev, uNext); // interior angle for convex polygon
    const tanHalf = Math.tan(alpha / 2) || 1; // guard against 0

    const ePrev = len(p, prev);
    const eNext = len(p, next);
    const tAllowed = Math.max(0, Math.min(ePrev, eNext));
    const tDesired = radius / tanHalf;
    const t = Math.min(tDesired, tAllowed);
    const rEff = t * tanHalf; // adjust radius if clamped

    const i1: [number, number] = [p[0] + uPrev[0] * t, p[1] + uPrev[1] * t];
    const i2: [number, number] = [p[0] + uNext[0] * t, p[1] + uNext[1] * t];
    return { i1, i2, r: rEff };
  });

  const p0 = corners[0].i2;
  let d = `M ${p0[0]} ${p0[1]}`;
  for (let k = 1; k <= n; k++) {
    const idx = k % n;
    const { i1, i2, r } = corners[idx];
    d += ` L ${i1[0]} ${i1[1]}`;
    d += ` A ${r} ${r} 0 0 1 ${i2[0]} ${i2[1]}`;
  }
  d += ' Z';
  return d;
};

const buildRoundedHexPath = (
  width: number,
  height: number,
  scaleInset: number,
  radius: number
) => {
  const w = Math.max(1, width);
  const h = Math.max(1, height);
  const cx = w / 2;
  const cy = h / 2;

  // Regular hexagon (pointy-top): fits max inside the rect
  // For pointy-top, width = sqrt(3) * r and height = 2 * r
  const rMax = Math.min(h / 2, w / Math.sqrt(3));
  const r = Math.max(1, rMax - Math.max(0, Math.min(scaleInset, rMax - 1)));

  const pts: Array<[number, number]> = [];
  for (let k = 0; k < 6; k++) {
    const deg = -90 + k * 60; // start at top vertex
    const radA = (deg * Math.PI) / 180;
    pts.push([cx + r * Math.cos(radA), cy + r * Math.sin(radA)]);
  }

  return buildRoundedPathFromPoints(pts, radius);
};

export const RoundedHexagon: React.FC<RoundedHexagonProps> = ({
  cornerDiagonal,
  cornerRadius = 12,
  stroke = '#111827',
  strokeWidth = 2,
  background = 'transparent',
  strokeGradient,
  strokeGradientAngle = 135,
  strokeGradientId,
  strokeGradientUnits = 'objectBoundingBox',
  strokeGradientCoords,
  strokeGradientStops,
  className,
  style,
  children,
}) => {
  const [elementRef, bounds] = useMeasure({ offsetSize: true });
  const width = bounds.width;
  const height = bounds.height;
  const gid = Math.random().toString(36).slice(2);

  const d = useMemo(
    () => buildRoundedHexPath(width, height, cornerDiagonal, cornerRadius),
    [width, height, cornerDiagonal, cornerRadius]
  );

  const clipPathValue = `path('${d}')`;
  const [x1, y1, x2, y2] = useMemo(() => {
    if (strokeGradientUnits === 'userSpaceOnUse' && strokeGradientCoords) {
      return [
        strokeGradientCoords.x1,
        strokeGradientCoords.y1,
        strokeGradientCoords.x2,
        strokeGradientCoords.y2,
      ];
    }
    const rad = (strokeGradientAngle * Math.PI) / 180;
    const cx = 0.5,
      cy = 0.5;
    const dx = Math.cos(rad) * 0.5;
    const dy = Math.sin(rad) * 0.5;
    return [cx - dx, cy - dy, cx + dx, cy + dy];
  }, [strokeGradientAngle, strokeGradientUnits, strokeGradientCoords]);

  return (
    <div ref={elementRef} className={className} style={{ position: 'relative', ...style }}>
      <div
        className='relative'
        style={{
          WebkitClipPath: clipPathValue as any,
          clipPath: clipPathValue as any,
          background,
          backdropFilter: `blur(12px)`,
        }}
      >
        {children}
      </div>

      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}
      >
        {!strokeGradientId && (strokeGradientStops?.length || strokeGradient?.length) && (
          <defs>
            <linearGradient id={`hex-stroke-${gid}`} x1={x1} y1={y1} x2={x2} y2={y2} gradientUnits={strokeGradientUnits}>
              {(strokeGradientStops && strokeGradientStops.length > 0
                ? strokeGradientStops
                : (strokeGradient || []).map((c, i, arr) => ({ color: c, offset: arr.length === 1 ? 0 : i / (arr.length - 1) }))
              ).map((s, i) => (
                <stop key={i} offset={`${((s.offset ?? 0) * 100).toFixed(3)}%`} stopColor={s.color} />
              ))}
            </linearGradient>
          </defs>
        )}
        <path
          d={d}
          fill="none"
          stroke={
            strokeGradientId
              ? `url(#${strokeGradientId})`
              : (strokeGradientStops?.length || strokeGradient?.length)
              ? `url(#hex-stroke-${gid})`
              : stroke
          }
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          shapeRendering="geometricPrecision"
        />
      </svg>
    </div>
  );
};

export const InnerShape = ({
  cornerDiagonal,
  cornerRadius,
  strokeWidth,
  children,
 }: { 
  cornerDiagonal: number;
  cornerRadius: number;
  strokeWidth: number;
  children?: React.ReactNode 
}) => {
  return (
    <RoundedOctagon
      cornerDiagonal={cornerDiagonal}
      cornerRadius={cornerRadius}
      strokeWidth={strokeWidth}
      strokeGradientUnits="userSpaceOnUse"
      strokeGradientCoords={{ x1: 285.703, y1: 18, x2: 198.399, y2: 206.09 }}
      strokeGradientStops={[
      { offset: 0, color: '#FFF085' },
      { offset: 0.843917, color: '#FFE10C' },
      ]}
    >
      <div className='absolute inset-0 bg-[#F0B100] opacity-[0.25]' />
      <div className='relative'>
        {children}
      </div>
    </RoundedOctagon>
  )
}

export const InnerShape2 = ({
  cornerDiagonal,
  cornerRadius,
  strokeWidth,
  children,
 }: { 
  cornerDiagonal: number;
  cornerRadius: number;
  strokeWidth: number;
  children?: React.ReactNode 
}) => {
  return (
    <RoundedOctagon
      cornerDiagonal={cornerDiagonal}
      cornerRadius={cornerRadius}
      strokeWidth={strokeWidth}
      strokeGradientUnits="userSpaceOnUse"
      strokeGradientCoords={{ x1: 285.703, y1: 18, x2: 198.399, y2: 206.09 }}
      strokeGradientStops={[
      { offset: 0, color: '#FFF08550' },
      { offset: 0.843917, color: '#FFE10C50' },
      ]}
    >
      <div className='absolute inset-0 bg-[#F0B100] opacity-[0.15]' />
      <div className='relative'>
        {children}
      </div>
    </RoundedOctagon>
  )
}


export const InnerShape3 = ({
  cornerDiagonal,
  cornerRadius,
  strokeWidth,
  children,
 }: { 
  cornerDiagonal: number;
  cornerRadius: number;
  strokeWidth: number;
  children?: React.ReactNode 
}) => {
  return (
    <RoundedOctagon
      cornerDiagonal={cornerDiagonal}
      cornerRadius={cornerRadius}
      strokeWidth={strokeWidth}
      strokeGradientUnits="userSpaceOnUse"
      strokeGradientCoords={{ x1: 285.703, y1: 18, x2: 198.399, y2: 206.09 }}
      strokeGradientStops={[
      { offset: 0, color: '#FFF085' },
      { offset: 0.843917, color: '#FFE10C' },
      ]}
    >
      <div className='absolute inset-0 bg-[#FFA90A] opacity-[0.40]' />
      <div className='relative'>
        {children}
      </div>
    </RoundedOctagon>
  )
}



export const InnerShape4 = ({
  cornerDiagonal,
  cornerRadius,
  strokeWidth,
  children,
 }: { 
  cornerDiagonal: number;
  cornerRadius: number;
  strokeWidth: number;
  children?: React.ReactNode 
}) => {
  return (
    <RoundedOctagon
      cornerDiagonal={cornerDiagonal}
      cornerRadius={cornerRadius}
      strokeWidth={strokeWidth}
      strokeGradientUnits="userSpaceOnUse"
      strokeGradientCoords={{ x1: 285.703, y1: 18, x2: 198.399, y2: 206.09 }}
      strokeGradientStops={[
      { offset: 0, color: '#F7A719' },
      { offset: 0.843917, color: '#F7A719' },
      ]}
    >
      <div className='absolute inset-0 bg-[#FFA90A] opacity-[0.75]' />
      <div className='relative'>
        {children}
      </div>
    </RoundedOctagon>
  )
}

export const HexagonContainerShape = ({
  cornerRadius,
  strokeWidth,
  children,
 }: { 
  cornerRadius: number;
  strokeWidth: number;
  children?: React.ReactNode 
}) => {
  return (
    <RoundedHexagon
      cornerDiagonal={0}
      cornerRadius={cornerRadius}
      strokeWidth={strokeWidth}
      strokeGradientUnits="userSpaceOnUse"
      strokeGradientCoords={{ x1: 285.703, y1: 18, x2: 198.399, y2: 206.09 }}
      strokeGradientStops={[
      { offset: 0, color: '#FFC364' },
      { offset: 0.264423, color: '#B97200' },
      { offset: 0.843917, color: '#A65F00' },
      ]}
    >
      <div className='absolute inset-0 bg-[#432004] opacity-[0.8]' />
      <div className='relative'>
        {children}
      </div>
    </RoundedHexagon>
  )
}


export const HexagonShape = ({
  cornerRadius,
  strokeWidth,
  children,
 }: { 
  cornerRadius: number;
  strokeWidth: number;
  children?: React.ReactNode 
}) => {
  return (
    <RoundedHexagon
      cornerDiagonal={0}
      cornerRadius={cornerRadius}
      strokeWidth={strokeWidth}
      strokeGradientUnits="userSpaceOnUse"
      strokeGradientCoords={{ x1: 285.703, y1: 18, x2: 198.399, y2: 206.09 }}
      strokeGradientStops={[
      { offset: 0, color: '#FFF085' },
      { offset: 0.843917, color: '#FFE10C' },
      ]}
    >
      <div className='absolute inset-0 bg-[#F0B100] opacity-[0.25]' />
      <div className='relative'>
        {children}
      </div>
    </RoundedHexagon>
  )
}
